import { memo } from 'react'
import { useTranslation } from 'react-i18next'
import type { LiveCheckinMonthTask, LiveCheckinTaskDefault } from '../types/check-in'
import { formatNumber } from '../helpers/format'
import { taskFallback } from '../helpers/task-labels'

interface Props {
  task: LiveCheckinMonthTask | null
  def?: LiveCheckinTaskDefault | null
}

/**
 * Card grande de uma tarefa (versão expandida pro carrossel — estilo
 * rocket-game-modal: emoji grande no centro, status, barra de progresso).
 */
export const TaskCard = memo(({ task, def }: Props) => {
  const { t } = useTranslation()
  const fallback = taskFallback(task?.liveCheckinTaskType ?? 'check_in')
  const name = def?.nameKey
    ? t(def.nameKey, { defaultValue: def.internalName ?? fallback.name })
    : (def?.internalName ?? fallback.name)
  const description = def?.descriptionKey
    ? t(def.descriptionKey, { defaultValue: def.internalDescription ?? '' })
    : (def?.internalDescription ?? null)
  const emoji = def?.emoji ?? fallback.emoji

  const target = task?.targetValue || def?.targetValue || 1
  const done = task ? Math.min(task.quantityCompleted ?? 0, target) : 0
  const pct = Math.min(100, (done / target) * 100)
  const isCompleted = !!task?.completed
  const pointReward = task?.pointReward ?? def?.pointReward ?? 0

  const statusStyle = isCompleted
    ? {
        bg: 'from-emerald-400/30 to-emerald-600/30 border-emerald-300/50',
        bar: 'from-emerald-300 to-emerald-500',
        chip: 'bg-emerald-500/80',
        label: 'Concluída',
      }
    : pct > 0
      ? {
          bg: 'from-violet-400/30 to-fuchsia-600/30 border-violet-300/50',
          bar: 'from-fuchsia-300 to-violet-500',
          chip: 'bg-violet-500/90',
          label: 'Em progresso',
        }
      : {
          bg: 'from-slate-400/30 to-slate-600/30 border-slate-300/50',
          bar: 'from-slate-300 to-slate-500',
          chip: 'bg-slate-500/80',
          label: 'Não iniciada',
        }

  return (
    <div className="w-full">
      {/* chip status */}
      <div className="mb-2 flex justify-center">
        <span
          className={`rounded-full px-3 py-0.5 text-[11px] font-semibold uppercase tracking-wider text-white ${statusStyle.chip}`}
        >
          {statusStyle.label}
        </span>
      </div>

      {/* emoji central com glow */}
      <div className="relative mx-auto flex h-32 w-32 items-center justify-center">
        <div
          aria-hidden
          className="absolute inset-0 rounded-full"
          style={{
            background:
              'radial-gradient(circle, rgba(255,170,80,0.35) 0%, rgba(255,170,80,0) 70%)',
          }}
        />
        <span className="relative text-7xl drop-shadow-[0_4px_20px_rgba(255,180,80,0.4)]">
          {emoji}
        </span>
      </div>

      {/* nome + descrição */}
      <div className="mt-3 text-center">
        <h2 className="text-lg font-extrabold text-white">{name}</h2>
        {description && (
          <p className="mt-0.5 text-xs text-white/70">{description}</p>
        )}
      </div>

      {/* card de progresso */}
      <div
        className={`mt-4 w-full rounded-2xl border bg-gradient-to-br p-4 backdrop-blur-md ${statusStyle.bg}`}
      >
        <div className="flex items-center justify-between text-sm font-semibold">
          <span>Progresso</span>
          <span className="rounded-full bg-white/15 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white">
            +{formatNumber(pointReward)} pts
          </span>
        </div>

        <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-black/30">
          <div
            className={`h-full rounded-full bg-gradient-to-r transition-[width] duration-700 ease-out ${statusStyle.bar}`}
            style={{ width: `${pct}%` }}
          />
        </div>

        <div className="mt-1.5 flex items-center justify-between text-[11px] text-white/80">
          <span>{formatNumber(done)} de {formatNumber(target)}</span>
          <span>{Math.round(pct)}%</span>
        </div>
      </div>
    </div>
  )
})

TaskCard.displayName = 'TaskCard'
